/**
 * Stories — list / editor / viewer.
 *
 * The Stories workspace is a full-screen overlay above the map. The map
 * stays mounted underneath because every slide change drives a flyTo on it
 * (and toggles user layers). This is the "scrollytelling" pattern used by
 * NYT graphics and Mapbox storytelling.
 */
import {
  listStories, listPublicStories, getStory, getPublicStory,
  createStory, updateStory, deleteStory,
  shareStory, unshareStory,
} from '../api/stories.js';
import { getLayer } from '../api/layers.js';
import { auth } from '../auth/state.js';
import { toast } from './toast.js';
import { cardSkeleton } from './skeleton.js';
import { emptyState, ICON_STORIES } from './emptyState.js';
import { renderMarkdown } from './markdown.js';

let host = null;            // list overlay (#storiesOverlay)
let editorDock = null;      // editor side panel (#storyEditorDock)
let mapRef = null;
let userLayersRef = null;
let switchBasemapRef = null;
let getCurrentBasemapRef = null;

let allLayers = [];          // cached layer index for layer pickers

// --- Public API ----------------------------------------------------------

export function initStoriesWorkspace({
  map, userLayers, switchBasemap, getCurrentBasemap, getAllLayers,
}) {
  mapRef = map;
  userLayersRef = userLayers;
  switchBasemapRef = switchBasemap;
  getCurrentBasemapRef = getCurrentBasemap;
  allLayers = [];
  // Lazy-load the layer index when needed
  getAllLayersFn = getAllLayers;

  // Auto-open public viewer when ?story=<token> is in the URL
  const params = new URLSearchParams(location.search);
  const token = params.get('story');
  if (token) openPublicStoryViewer(token);
}

let getAllLayersFn = async () => [];

export async function openStories() {
  if (!auth.isAuthenticated()) {
    toast('Sign in to use Stories');
    return;
  }
  ensureHost();
  await renderList();
  show();
}

// --- Overlay shell -------------------------------------------------------

function ensureHost() {
  if (host) return;
  document.body.insertAdjacentHTML('beforeend', `
    <div class="st-overlay" id="storiesOverlay">
      <div class="st-shell" id="storiesShell"></div>
    </div>
    <style>
      .st-overlay {
        position: fixed; inset: 0; z-index: 150;
        background: rgba(2, 21, 33, 0.92);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        display: none;
      }
      .st-overlay.show { display: block; animation: stFade .25s var(--ease-out); }
      @keyframes stFade { from { opacity: 0 } to { opacity: 1 } }

      .st-shell { display: flex; flex-direction: column; height: 100vh; }

      .st-head {
        display: flex; align-items: baseline; gap: 14px;
        padding: 18px 28px 14px;
        border-bottom: 1px solid var(--border);
        background: var(--bg2);
      }
      .st-head h2 {
        font-family: var(--font-display); font-weight: 500;
        font-size: 22px; letter-spacing: -0.015em; margin: 0;
      }
      .st-head .st-sub {
        font-family: var(--font-display); font-style: italic;
        color: var(--text2); font-size: 13px;
      }
      .st-head .st-spacer { flex: 1; }

      .st-body { flex: 1; overflow-y: auto; padding: 24px 28px 40px; }

      /* List view */
      .st-list-grid {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 14px; max-width: 1200px;
      }
      .st-card {
        background: var(--bg3); border: 1px solid var(--border);
        border-radius: var(--radius-lg); padding: 16px 18px;
        cursor: pointer; transition: border-color .15s var(--ease), transform .15s var(--ease);
      }
      .st-card:hover { border-color: var(--border2); transform: translateY(-1px); }
      .st-card-title {
        font-family: var(--font-display); font-weight: 500; font-size: 17px;
        letter-spacing: -0.015em; line-height: 1.25;
      }
      .st-card-summary {
        font-size: 12.5px; color: var(--text2); margin-top: 8px; line-height: 1.5;
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .st-card-meta {
        display: flex; gap: 10px; font-size: 10.5px; color: var(--text3);
        margin-top: 12px; font-feature-settings: 'tnum';
      }
      .st-card-shared {
        display: inline-flex; align-items: center; gap: 4px;
        color: var(--primary); font-weight: 600;
      }
      .st-card.create {
        background: transparent; border-style: dashed; border-color: var(--border3);
        color: var(--text3); display: grid; place-items: center; min-height: 140px;
        font-family: var(--font-display); font-style: italic;
      }
      .st-card.create:hover { border-color: var(--primary); color: var(--primary); }

      /* Slide rows — used inside the editor dock */
      .st-slide-row {
        padding: 12px 14px; border: 1px solid var(--border);
        border-radius: var(--radius); margin-bottom: 8px; cursor: pointer;
        transition: background .15s var(--ease), border-color .15s var(--ease);
        position: relative;
      }
      .st-slide-row:hover { background: var(--bg3); }
      .st-slide-row.active { border-color: var(--primary); background: var(--primary-dim); }
      .st-slide-row.st-drag-src { opacity: 0.45; }
      .st-slide-row.st-drop-above { box-shadow: 0 -2px 0 var(--primary); }
      .st-slide-row.st-drop-below { box-shadow: 0  2px 0 var(--primary); }
      .st-slide-num {
        font-family: var(--mono); font-size: 10px; color: var(--text3);
        font-feature-settings: 'tnum';
      }
      .st-slide-title {
        font-size: 13px; font-weight: 500; color: var(--text); margin-top: 2px;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .st-slide-actions {
        position: absolute; top: 8px; right: 8px; display: flex; gap: 4px;
        opacity: 0; transition: opacity .15s var(--ease);
      }
      .st-slide-row:hover .st-slide-actions { opacity: 1; }
      .st-slide-actions button {
        width: 22px; height: 22px; border-radius: var(--radius-sm); border: none;
        background: var(--bg4); color: var(--text3); display: grid; place-items: center; cursor: pointer;
      }
      .st-slide-actions button:hover { color: var(--text); background: var(--bg5); }
      .st-add {
        margin: 4px 10px 14px; padding: 10px;
        border: 1px dashed var(--border3); border-radius: var(--radius);
        color: var(--text3); background: transparent; cursor: pointer;
        text-align: center; font-size: 12px;
        font-family: var(--font-display); font-style: italic;
        transition: color .15s var(--ease), border-color .15s var(--ease);
      }
      .st-add:hover { color: var(--primary); border-color: var(--primary); }

      /* ---- Immersive viewing mode ---------------------------------------
         When a story is playing, fade away the chrome (nav, sidebars,
         map controls) so the map + slide panel are the focus. */
      body.story-viewing .nav { display: none; }
      body.story-viewing .layout { height: 100vh !important; }
      body.story-viewing .sidebar,
      body.story-viewing .rpanel {
        width: 0 !important; min-width: 0 !important;
        overflow: hidden;
        transition: width .35s var(--ease), min-width .35s var(--ease);
      }
      body.story-viewing .map-tools,
      body.story-viewing .map-status,
      body.story-viewing .basemap-sw,
      body.story-viewing .map-legend,
      body.story-viewing .maplibregl-ctrl-top-right,
      body.story-viewing .maplibregl-ctrl-bottom-right {
        opacity: 0; pointer-events: none;
        transition: opacity .35s var(--ease);
      }

      /* Viewer */
      .st-viewer {
        position: fixed; inset: 0; pointer-events: none;
      }
      /* Scroll container that holds every slide stacked vertically. The
         user's natural scrolling drives slide changes — IntersectionObserver
         in JS detects which card is centered and triggers the map fly. */
      .st-viewer-scroll {
        pointer-events: auto;
        position: absolute; left: 32px; top: 0; bottom: 0;
        width: 420px; max-width: calc(100vw - 96px);
        overflow-y: auto;
        scroll-behavior: smooth;
        scroll-snap-type: y proximity;
        scrollbar-width: thin;
        scrollbar-color: rgba(255,255,255,0.18) transparent;
        animation: stFade .35s var(--ease-out);
      }
      .st-viewer-scroll::-webkit-scrollbar { width: 6px; }
      .st-viewer-scroll::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.16); border-radius: 3px;
      }

      /* Each slide takes a near-full-height block so the map has time to
         finish flying between transitions. */
      .st-slide-card {
        min-height: 100vh;
        display: flex; flex-direction: column; justify-content: center;
        padding: 8vh 0;
        scroll-snap-align: center;
      }
      .st-slide-card-inner {
        background: rgba(15, 20, 29, 0.94);
        border: 1px solid var(--border2);
        border-radius: var(--radius-xl);
        padding: 28px 30px 26px;
        backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
        box-shadow: var(--shadow-lg);
        transition: opacity .35s var(--ease), transform .35s var(--ease);
        opacity: 0.45;
        transform: scale(0.985);
      }
      .st-slide-card.is-active .st-slide-card-inner {
        opacity: 1;
        transform: scale(1);
      }
      @media (max-width: 900px) {
        .st-viewer-scroll {
          left: 0; right: 0; width: auto; max-width: none;
          padding: 0 16px;
        }
      }
      .st-viewer-num {
        font-family: var(--mono); font-size: 10.5px; color: var(--text3);
        font-feature-settings: 'tnum'; letter-spacing: 0.06em;
        margin-bottom: 8px;
      }
      .st-viewer-title {
        font-family: var(--font-display); font-weight: 500;
        font-size: 30px; letter-spacing: -0.025em; line-height: 1.1;
        color: var(--text); margin-bottom: 14px;
      }
      .st-viewer-body {
        font-size: 14.5px; line-height: 1.7; color: var(--text2);
        white-space: pre-wrap;
      }
      /* Markdown-rendered viewer body */
      .st-md p { margin: 0 0 14px; }
      .st-md p:last-child { margin-bottom: 0; }
      .st-md h1, .st-md h2, .st-md h3, .st-md h4 {
        font-family: var(--font-display); font-weight: 500;
        letter-spacing: -0.012em; color: var(--text);
        margin: 18px 0 8px; line-height: 1.25;
      }
      .st-md h1 { font-size: 22px; }
      .st-md h2 { font-size: 19px; }
      .st-md h3 { font-size: 16px; }
      .st-md h4 { font-size: 14.5px; }
      .st-md a {
        color: var(--primary); text-decoration: underline;
        text-decoration-thickness: 1px; text-underline-offset: 2px;
      }
      .st-md a:hover { color: var(--primary-hover); }
      .st-md strong { color: var(--text); font-weight: 600; }
      .st-md em { font-style: italic; color: var(--text); }
      .st-md ul, .st-md ol { margin: 0 0 14px 20px; padding: 0; }
      .st-md li { margin-bottom: 4px; }
      .st-md blockquote {
        border-left: 2px solid var(--primary);
        padding: 4px 0 4px 14px; margin: 14px 0;
        font-family: var(--font-display); font-style: italic;
        color: var(--text); font-size: 15.5px; line-height: 1.55;
      }
      .st-md code {
        font-family: var(--mono); font-size: 12.5px;
        background: var(--bg4); padding: 1px 6px; border-radius: 3px;
        color: var(--text);
      }
      .st-md hr { border: none; border-top: 1px solid var(--border); margin: 18px 0; }
      /* Reset whitespace in markdown — leave to <p>/<br> */
      .st-md { white-space: normal; }
      /* Side rail — stacked tick marks acting as a clickable progress bar.
         Each tick maps to a slide and scrolls the viewer to it. */
      .st-rail {
        pointer-events: auto;
        position: absolute; right: 28px; top: 50%; transform: translateY(-50%);
        display: flex; flex-direction: column; gap: 8px;
        z-index: 11;
      }
      .st-rail-dot {
        width: 6px; height: 22px; padding: 0; border-radius: 3px;
        background: rgba(255,255,255,0.22); border: none; cursor: pointer;
        transition: background .2s var(--ease), height .25s var(--ease), width .2s var(--ease);
      }
      .st-rail-dot:hover { background: rgba(255,255,255,0.55); }
      .st-rail-dot.active {
        background: var(--primary); height: 36px; width: 8px;
      }

      /* Scroll hint — fades in once and disappears after the first scroll. */
      .st-vn-hint {
        position: absolute; bottom: 28px; left: 50%; transform: translateX(-50%);
        pointer-events: none;
        display: flex; gap: 12px; align-items: center;
        font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.12em;
        color: var(--text3); text-transform: uppercase;
        background: rgba(15, 20, 29, 0.7);
        padding: 8px 14px; border-radius: 999px;
        border: 1px solid var(--border);
        backdrop-filter: blur(10px);
        transition: opacity .4s var(--ease);
      }
      .st-vn-hint.fade { opacity: 0; }
      .st-vn-hint kbd {
        font-family: var(--mono); font-size: 9.5px;
        padding: 1px 5px; border-radius: 3px;
        background: var(--bg4); color: var(--text2);
        border: 1px solid var(--border);
      }
      .st-viewer-close {
        position: absolute; top: 16px; right: 32px;
        z-index: 10; pointer-events: auto;
        background: rgba(15, 20, 29, 0.94); border: 1px solid var(--border2);
        color: var(--text2); width: 36px; height: 36px;
        border-radius: var(--radius); cursor: pointer;
        display: grid; place-items: center;
        backdrop-filter: blur(16px);
      }
      .st-viewer-close:hover { color: var(--text); }
    </style>
  `);
  host = document.getElementById('storiesOverlay');
}

function show() { host.classList.add('show'); }
function hide() { host.classList.remove('show'); }

// --- Editor dock (side panel — map stays visible behind it) -------------

function ensureEditorDock() {
  if (editorDock) return;
  document.body.insertAdjacentHTML('beforeend', `
    <aside class="st-dock" id="storyEditorDock"></aside>
    <style>
      .st-dock {
        position: fixed; top: 52px; right: 0; bottom: 0;
        width: 420px; max-width: 100vw;
        background: var(--bg2);
        border-left: 1px solid var(--border2);
        box-shadow: var(--shadow-lg);
        z-index: 140;
        display: none; flex-direction: column;
        overflow: hidden;
      }
      .st-dock.show { display: flex; animation: stDockIn .22s var(--ease-out); }
      @keyframes stDockIn {
        from { transform: translateX(24px); opacity: 0 }
        to   { transform: none; opacity: 1 }
      }
      /* Shift right-side map controls left so they don't sit under the dock */
      body.story-editor-open .basemap-sw { right: 432px !important; }
      body.story-editor-open .maplibregl-ctrl-top-right,
      body.story-editor-open .maplibregl-ctrl-bottom-right { right: 420px !important; }

      .st-dock-head {
        display: flex; align-items: center; gap: 8px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--border);
        background: var(--bg3);
      }
      .st-dock-head .title {
        flex: 1; font-family: var(--font-display); font-weight: 500;
        font-size: 14px; letter-spacing: -0.01em;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        color: var(--text);
      }
      .st-dock-head .sub {
        font-family: var(--font-display); font-style: italic;
        font-size: 11px; color: var(--text3);
      }
      .st-dock-back {
        background: transparent; border: 1px solid var(--border);
        color: var(--text2); padding: 5px 10px; border-radius: var(--radius-sm);
        font-size: 11.5px; cursor: pointer;
        transition: color .15s var(--ease), border-color .15s var(--ease);
      }
      .st-dock-back:hover { color: var(--text); border-color: var(--border2); }

      .st-dock-actions {
        display: flex; gap: 6px; padding: 10px 16px;
        border-bottom: 1px solid var(--border);
      }
      .st-dock-actions .btn-sm { flex: 1; padding: 6px 8px; font-size: 11px; }

      .st-dock-body { flex: 1; overflow-y: auto; }

      .st-dock-section {
        padding: 14px 18px;
        border-bottom: 1px solid var(--border);
      }
      .st-dock-section h4 {
        font-size: 9.5px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 0.14em; color: var(--text3);
        margin: 0 0 10px;
      }
      .st-dock-section input.story-title,
      .st-dock-section input.slide-title {
        width: 100%; background: transparent; border: none;
        font-family: var(--font-display); font-weight: 500;
        font-size: 18px; letter-spacing: -0.015em;
        color: var(--text); padding: 4px 0;
        border-bottom: 1px solid transparent;
      }
      .st-dock-section input.story-title:focus,
      .st-dock-section input.slide-title:focus {
        outline: none; border-bottom-color: var(--primary);
      }
      .st-dock-section textarea.story-summary {
        width: 100%; background: transparent; border: none; resize: none;
        font-family: var(--font); color: var(--text2); font-size: 12.5px;
        line-height: 1.5; padding: 6px 0 0; min-height: 36px;
      }
      .st-dock-section textarea.story-summary:focus { outline: none; }
      .st-dock-section textarea.slide-body {
        width: 100%; background: transparent;
        border: 1px solid var(--border); border-radius: var(--radius);
        color: var(--text); font-family: var(--font); font-size: 13px;
        line-height: 1.6; padding: 10px 12px; min-height: 140px; resize: vertical;
        margin-top: 10px;
      }
      .st-dock-section textarea.slide-body:focus {
        outline: none; border-color: var(--primary);
      }
      .st-dock-mdhint {
        font-family: var(--font-display); font-style: italic;
        font-size: 10.5px; color: var(--text3); margin-top: 6px;
      }

      .st-dock-slides {
        max-height: 220px; overflow-y: auto; padding: 2px;
      }
      .st-dock-mapstate {
        display: flex; gap: 8px; align-items: center;
        padding: 10px 12px; background: var(--bg3);
        border: 1px solid var(--border); border-radius: var(--radius);
        margin-top: 12px;
      }
      .st-dock-mapstate .meta {
        flex: 1; font-size: 10.5px; color: var(--text2);
        font-family: var(--mono); font-feature-settings: 'tnum';
        line-height: 1.5; min-width: 0;
      }
      .st-dock-capture-row {
        display: flex; gap: 8px; margin-top: 8px;
      }
      .st-dock-capture-row .btn-sm { flex: 1; }
      .st-dock-empty {
        color: var(--text3); font-size: 12.5px; font-style: italic;
        font-family: var(--font-display);
      }
    </style>
  `);
  editorDock = document.getElementById('storyEditorDock');
  editorDock.addEventListener('click', editorClickHandler);
}

function showEditorDock() {
  hide();
  document.body.classList.add('story-editor-open');
  editorDock.classList.add('show');
}

function closeEditorDock() {
  document.body.classList.remove('story-editor-open');
  editorDock?.classList.remove('show');
}

// =========================================================================
// List view
// =========================================================================

async function renderList() {
  const shell = document.getElementById('storiesShell');
  shell.innerHTML = `
    <header class="st-head">
      <div>
        <h2>Stories</h2>
        <div class="st-sub">Map-driven narratives — yours and the public ones</div>
      </div>
      <div class="st-spacer"></div>
      <button class="btn" data-action="closeStories">Close</button>
    </header>
    <div class="st-body" id="stListBody">
      <h3 style="font-family:var(--font-display);font-weight:500;font-size:15px;color:var(--text2);margin:4px 0 14px">My stories</h3>
      <div class="st-list-grid">${cardSkeleton(3)}</div>
    </div>
  `;

  let mine = [], pub = [];
  try { mine = await listStories(); } catch (e) { toast(e.message); }
  try { pub  = await listPublicStories(); } catch {}

  const myCards = mine.map(renderCard).join('');
  const ownIds = new Set(mine.map(s => s.id));
  const publicCards = pub.filter(s => !ownIds.has(s.id)).map(renderCard).join('');

  const noStoriesAtAll = mine.length === 0 && publicCards === '';

  document.getElementById('stListBody').innerHTML = noStoriesAtAll
    ? emptyState({
        icon:  ICON_STORIES,
        title: 'Tell your first story',
        body:  'A story is a sequence of slides — each one a moment in the map. Capture views as you compose, then publish a link anyone can read.',
        action: '<button class="btn btn-primary" data-action="createStory">+ new story</button>',
        size:   'large',
      })
    : `
      <h3 style="font-family:var(--font-display);font-weight:500;font-size:15px;color:var(--text2);margin:4px 0 14px">My stories</h3>
      <div class="st-list-grid">
        <div class="st-card create" data-action="createStory">+ new story</div>
        ${myCards}
      </div>
      ${publicCards ? `
        <h3 style="font-family:var(--font-display);font-weight:500;font-size:15px;color:var(--text2);margin:36px 0 14px">Published</h3>
        <div class="st-list-grid">${publicCards}</div>
      ` : ''}
    `;

  shell.addEventListener('click', listClickHandler);
}

function renderCard(s) {
  const updated = new Date(s.updated_at).toLocaleDateString();
  return `
    <div class="st-card" data-open-story="${s.id}">
      <div class="st-card-title">${escape(s.title)}</div>
      <div class="st-card-summary">${escape(s.summary || '—')}</div>
      <div class="st-card-meta">
        <span>${s.slide_count} slide${s.slide_count === 1 ? '' : 's'}</span>
        <span>·</span>
        <span>${updated}</span>
        ${s.is_public ? '<span style="flex:1"></span><span class="st-card-shared">● shared</span>' : ''}
      </div>
      ${s.owner_email && s.owner_id !== auth.getUser()?.id ? `<div style="font-size:10.5px;color:var(--text3);margin-top:6px;font-style:italic;font-family:var(--font-display)">by ${escape(s.owner_full_name || s.owner_email)}</div>` : ''}
    </div>
  `;
}

async function listClickHandler(e) {
  const create = e.target.closest('[data-action="createStory"]');
  if (create) { await createNewStory(); return; }

  const open = e.target.closest('[data-open-story]');
  if (open) {
    const id = +open.dataset.openStory;
    await openStoryById(id);
  }
}

async function createNewStory() {
  const title = prompt('Story title:', 'Untitled story');
  if (!title) return;
  try {
    const created = await createStory({ title, slides: [] });
    await openStoryEditor(created);
  } catch (e) { toast(e.message); }
}

async function openStoryById(id) {
  try {
    const story = await getStory(id);
    const me = auth.getUser();
    if (me && story.owner_id === me.id) await openStoryEditor(story);
    else                                  await openStoryViewerFor(story);
  } catch (e) { toast(e.message); }
}

// =========================================================================
// Editor
// =========================================================================

let editorState = null;        // { story, currentSlideId }
let saveTimer = null;

async function openStoryEditor(story) {
  editorState = { story, currentSlideId: story.slides[0]?.id || null };
  await ensureLayerIndex();
  renderEditor();
}

function renderEditor() {
  const { story, currentSlideId } = editorState;
  const slide = story.slides.find(s => s.id === currentSlideId);

  ensureEditorDock();
  showEditorDock();

  editorDock.innerHTML = `
    <header class="st-dock-head">
      <button class="st-dock-back" data-action="backToList">← Stories</button>
      <div class="title">${escape(story.title)}</div>
      <div class="sub">${story.slides.length} slide${story.slides.length === 1 ? '' : 's'}</div>
    </header>
    <div class="st-dock-actions">
      <button class="btn btn-sm" data-action="previewStory">▶ Preview</button>
      ${story.is_public
        ? `<button class="btn btn-sm" data-action="copyShareLink">↗ Link</button>
           <button class="btn btn-sm" data-action="unshareStory">Unshare</button>`
        : `<button class="btn btn-sm btn-primary" data-action="shareStory">↗ Publish</button>`}
      <button class="btn btn-sm" data-action="deleteStory" style="color:var(--text3);border-color:transparent">Delete</button>
    </div>

    <div class="st-dock-body">
      <section class="st-dock-section">
        <h4>Story</h4>
        <input class="story-title" id="stTitleInput" value="${escape(story.title)}" placeholder="Title">
        <textarea class="story-summary" id="stSummaryInput" placeholder="A short summary…">${escape(story.summary || '')}</textarea>
      </section>

      <section class="st-dock-section">
        <h4>Slides</h4>
        <div class="st-dock-slides" id="stSlideList">
          ${story.slides.map((s, i) => `
            <div class="st-slide-row ${s.id === currentSlideId ? 'active' : ''}"
                 data-slide-id="${s.id}" draggable="true">
              <div class="st-slide-num">SLIDE ${String(i + 1).padStart(2, '0')}</div>
              <div class="st-slide-title">${escape(s.title || '(untitled)')}</div>
              <div class="st-slide-actions">
                <button data-move-up="${s.id}" title="Move up">↑</button>
                <button data-move-down="${s.id}" title="Move down">↓</button>
                <button data-delete-slide="${s.id}" title="Delete">×</button>
              </div>
            </div>
          `).join('')}
        </div>
        <button class="st-add" data-action="addSlide">+ add slide</button>
      </section>

      ${slide ? `
        <section class="st-dock-section">
          <h4>Slide content</h4>
          <input class="slide-title" id="stSlideTitle" value="${escape(slide.title || '')}" placeholder="Slide title…">
          <textarea class="slide-body" id="stSlideBody" placeholder="Write the narrative for this moment in the map…">${escape(slide.body || '')}</textarea>
          <div class="st-dock-mdhint">Markdown supported · **bold**, *italic*, [link](url), # heading</div>
        </section>
        <section class="st-dock-section">
          <h4>Map state</h4>
          <div class="st-dock-mdhint" style="margin:0 0 10px">
            Pan, zoom, switch basemap and toggle layers on the map, then capture this view for the slide.
          </div>
          ${renderMapStateBlock(slide)}
        </section>
      ` : `
        <section class="st-dock-section">
          <div class="st-dock-empty">Add a slide to get started.</div>
        </section>
      `}
    </div>
  `;

  if (slide) attachSlideEditorHandlers();
  document.getElementById('stTitleInput')?.addEventListener('input', scheduleSave);
  document.getElementById('stSummaryInput')?.addEventListener('input', scheduleSave);
  attachDragHandlers();
}

function renderMapStateBlock(slide) {
  const { center, zoom, basemap, visible_layers } = slide.map || {};
  return `
    <div class="st-dock-mapstate">
      <div class="meta">
        ${center
          ? `${center[0].toFixed(3)}, ${center[1].toFixed(3)} · z${zoom?.toFixed?.(1) ?? '–'}<br>${basemap || '–'} · ${(visible_layers || []).length} layer${(visible_layers || []).length === 1 ? '' : 's'}`
          : 'No state captured yet'}
      </div>
    </div>
    <div class="st-dock-capture-row">
      <button class="btn-sm btn-outline" data-action="restoreSlideState" ${!center ? 'disabled' : ''}>Restore</button>
      <button class="btn-sm btn-run" data-action="captureSlideState">Capture current view</button>
    </div>
  `;
}

// ---- Drag-and-drop reorder ---------------------------------------------

let dragSrcId = null;

function attachDragHandlers() {
  const list = document.getElementById('stSlideList');
  if (!list) return;

  list.addEventListener('dragstart', e => {
    const row = e.target.closest('.st-slide-row');
    if (!row) return;
    dragSrcId = row.dataset.slideId;
    row.classList.add('st-drag-src');
    e.dataTransfer.effectAllowed = 'move';
    // For Firefox, dataTransfer must be set or drag fails silently
    e.dataTransfer.setData('text/plain', dragSrcId);
  });

  list.addEventListener('dragend', () => {
    list.querySelectorAll('.st-drag-src,.st-drop-above,.st-drop-below')
        .forEach(el => el.classList.remove('st-drag-src','st-drop-above','st-drop-below'));
    dragSrcId = null;
  });

  list.addEventListener('dragover', e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const row = e.target.closest('.st-slide-row');
    if (!row || row.dataset.slideId === dragSrcId) return;
    list.querySelectorAll('.st-drop-above,.st-drop-below')
        .forEach(el => el.classList.remove('st-drop-above','st-drop-below'));
    const rect = row.getBoundingClientRect();
    const above = (e.clientY - rect.top) < rect.height / 2;
    row.classList.add(above ? 'st-drop-above' : 'st-drop-below');
  });

  list.addEventListener('drop', e => {
    e.preventDefault();
    const row = e.target.closest('.st-slide-row');
    if (!row || !dragSrcId || row.dataset.slideId === dragSrcId) return;
    const slides = editorState.story.slides;
    const fromIdx = slides.findIndex(s => s.id === dragSrcId);
    let toIdx     = slides.findIndex(s => s.id === row.dataset.slideId);
    if (fromIdx === -1 || toIdx === -1) return;
    const rect = row.getBoundingClientRect();
    const above = (e.clientY - rect.top) < rect.height / 2;
    if (!above) toIdx += 1;
    if (toIdx > fromIdx) toIdx -= 1;   // adjust for the removal
    const [moved] = slides.splice(fromIdx, 1);
    slides.splice(toIdx, 0, moved);
    renderEditor();
    scheduleSave();
  });
}

function attachSlideEditorHandlers() {
  document.getElementById('stSlideTitle')?.addEventListener('input', scheduleSave);
  document.getElementById('stSlideBody')?.addEventListener('input', scheduleSave);
}

async function editorClickHandler(e) {
  const target = e.target;
  if (target.closest('[data-action="backToList"]')) {
    await flushSave();
    closeEditorDock();
    editorState = null;
    show();
    await renderList();
    return;
  }
  if (target.closest('[data-action="addSlide"]')) {
    addSlide(); return;
  }
  const slideRow = target.closest('[data-slide-id]');
  if (slideRow && !target.closest('.st-slide-actions')) {
    await flushSave();
    editorState.currentSlideId = slideRow.dataset.slideId;
    renderEditor();
    return;
  }
  const del = target.closest('[data-delete-slide]');
  if (del) { deleteSlide(del.dataset.deleteSlide); return; }
  const up = target.closest('[data-move-up]');
  if (up) { moveSlide(up.dataset.moveUp, -1); return; }
  const down = target.closest('[data-move-down]');
  if (down) { moveSlide(down.dataset.moveDown, +1); return; }
  if (target.closest('[data-action="captureSlideState"]')) { captureSlideState(); return; }
  if (target.closest('[data-action="restoreSlideState"]')) { restoreSlideState(); return; }
  if (target.closest('[data-action="previewStory"]'))      { await openStoryViewerFor(editorState.story); return; }
  if (target.closest('[data-action="shareStory"]'))        { await doShare(); return; }
  if (target.closest('[data-action="unshareStory"]'))      { await doUnshare(); return; }
  if (target.closest('[data-action="copyShareLink"]'))     { copyShareLink(); return; }
  if (target.closest('[data-action="deleteStory"]'))       { await doDeleteStory(); return; }
}

function addSlide() {
  const slide = {
    id: 's_' + Math.random().toString(36).slice(2, 10),
    title: '',
    body: '',
    map: captureMapStateNow(),
  };
  editorState.story.slides.push(slide);
  editorState.currentSlideId = slide.id;
  renderEditor();
  scheduleSave();
}

function deleteSlide(id) {
  if (!confirm('Delete this slide?')) return;
  const story = editorState.story;
  const idx = story.slides.findIndex(s => s.id === id);
  story.slides.splice(idx, 1);
  if (editorState.currentSlideId === id) {
    editorState.currentSlideId = story.slides[Math.max(0, idx - 1)]?.id || null;
  }
  renderEditor();
  scheduleSave();
}

function moveSlide(id, dir) {
  const slides = editorState.story.slides;
  const i = slides.findIndex(s => s.id === id);
  const j = i + dir;
  if (j < 0 || j >= slides.length) return;
  [slides[i], slides[j]] = [slides[j], slides[i]];
  renderEditor();
  scheduleSave();
}

function captureMapStateNow() {
  const c = mapRef.getCenter();
  return {
    basemap: getCurrentBasemapRef(),
    center:  [c.lng, c.lat],
    zoom:    mapRef.getZoom(),
    pitch:   mapRef.getPitch(),
    bearing: mapRef.getBearing(),
    visible_layers: [...userLayersRef.loaded.keys()],
  };
}

function captureSlideState() {
  const slide = editorState.story.slides.find(s => s.id === editorState.currentSlideId);
  if (!slide) return;
  slide.map = captureMapStateNow();
  renderEditor();
  scheduleSave();
  toast('Map state captured for this slide');
}

async function restoreSlideState() {
  const slide = editorState.story.slides.find(s => s.id === editorState.currentSlideId);
  if (!slide?.map) return;
  await applyMapState(slide.map);
}

function scheduleSave() {
  // Pull latest values from the editor inputs into editorState.story
  const titleEl = document.getElementById('stTitleInput');
  const sumEl   = document.getElementById('stSummaryInput');
  const slideTitleEl = document.getElementById('stSlideTitle');
  const slideBodyEl  = document.getElementById('stSlideBody');
  if (titleEl) editorState.story.title = titleEl.value;
  if (sumEl)   editorState.story.summary = sumEl.value;
  if (slideTitleEl || slideBodyEl) {
    const slide = editorState.story.slides.find(s => s.id === editorState.currentSlideId);
    if (slide) {
      if (slideTitleEl) slide.title = slideTitleEl.value;
      if (slideBodyEl)  slide.body  = slideBodyEl.value;
    }
  }
  clearTimeout(saveTimer);
  saveTimer = setTimeout(flushSave, 600);
}

async function flushSave() {
  clearTimeout(saveTimer);
  if (!editorState) return;
  const { story } = editorState;
  try {
    const updated = await updateStory(story.id, {
      title: story.title,
      summary: story.summary,
      slides: story.slides,
    });
    editorState.story = { ...updated };
  } catch (e) {
    toast(`Save failed: ${e.message}`);
  }
}

async function doShare() {
  await flushSave();
  const updated = await shareStory(editorState.story.id);
  editorState.story = updated;
  renderEditor();
  copyShareLink();
}

async function doUnshare() {
  if (!confirm('Make this story private? Existing share links will stop working.')) return;
  const updated = await unshareStory(editorState.story.id);
  editorState.story = updated;
  renderEditor();
  toast('Story is private');
}

function copyShareLink() {
  const token = editorState.story.public_token;
  if (!token) return;
  const url = `${location.origin}${location.pathname}?story=${token}`;
  navigator.clipboard.writeText(url);
  toast('Share link copied');
}

async function doDeleteStory() {
  if (!confirm(`Delete "${editorState.story.title}"? This cannot be undone.`)) return;
  await deleteStory(editorState.story.id);
  editorState = null;
  closeEditorDock();
  show();
  await renderList();
}

// =========================================================================
// Viewer
// =========================================================================

let viewerState = null;     // { story, index }

async function openStoryViewerFor(story) {
  await ensureLayerIndex();
  viewerState = { story, index: 0 };
  hide();                                // hide list overlay
  closeEditorDock();                     // and the editor dock — viewer needs unobstructed map
  enterImmersive();
  renderViewer();
  await applySlide(0);
}

async function openPublicStoryViewer(token) {
  ensureHost();
  await ensureLayerIndex();
  let story;
  try { story = await getPublicStory(token); }
  catch (e) { toast(e.message); return; }
  viewerState = { story, index: 0, isPublic: true };
  enterImmersive();
  renderViewer();
  await applySlide(0);
}

function enterImmersive() {
  document.body.classList.add('story-viewing');
  // Trigger map resize after layout transitions so MapLibre fills the new area.
  setTimeout(() => mapRef?.resize?.(), 380);
}

function exitImmersive() {
  document.body.classList.remove('story-viewing');
  setTimeout(() => mapRef?.resize?.(), 380);
}

// Module-level so closeViewer can disconnect cleanly. The observer watches
// the slide cards inside the scrolling panel and fires applySlide when one
// crosses the centre of the viewport.
let slideObserver = null;
let hintFadeTimer = null;

function renderViewer() {
  const { story } = viewerState;
  const total = story.slides.length;

  // Always render fresh — the viewer DOM holds every slide stacked, so we
  // rebuild it from scratch when a story is opened or re-previewed.
  document.getElementById('storyViewer')?.remove();

  const noSlides = total === 0;
  document.body.insertAdjacentHTML('beforeend', `
    <div id="storyViewer" class="st-viewer">
      <button class="st-viewer-close" data-action="closeViewer" aria-label="Close story">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
      <div class="st-viewer-scroll" id="stViewerScroll">
        ${noSlides ? `
          <section class="st-slide-card is-active">
            <div class="st-slide-card-inner">
              <div class="st-viewer-num">EMPTY</div>
              <div class="st-viewer-title">${escape(story.title)}</div>
              <div class="st-viewer-body">No slides yet. Add one in the editor to start telling your story.</div>
            </div>
          </section>
        ` : story.slides.map((slide, i) => `
          <section class="st-slide-card ${i === 0 ? 'is-active' : ''}" data-slide-index="${i}">
            <div class="st-slide-card-inner">
              <div class="st-viewer-num">SLIDE ${String(i + 1).padStart(2, '0')} · ${String(total).padStart(2, '0')}</div>
              <div class="st-viewer-title">${escape(slide.title || story.title)}</div>
              <div class="st-viewer-body st-md">${renderMarkdown(slide.body || '')}</div>
            </div>
          </section>
        `).join('')}
      </div>
      ${total > 1 ? `
        <div class="st-rail">
          ${story.slides.map((_, i) => `
            <button class="st-rail-dot ${i === 0 ? 'active' : ''}"
                    data-jump-slide="${i}" aria-label="Slide ${i + 1}"></button>
          `).join('')}
        </div>
      ` : ''}
      ${total > 1 ? `
        <div class="st-vn-hint" id="stVnHint">
          <span><kbd>↓</kbd> scroll</span>
          <span>or <kbd>←</kbd><kbd>→</kbd></span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      ` : ''}
    </div>
  `);
  const viewer = document.getElementById('storyViewer');
  viewer.addEventListener('click', viewerClickHandler);
  document.addEventListener('keydown', viewerKeyHandler);

  setupScrollObserver();
  scheduleHintFade();
}

function setupScrollObserver() {
  if (slideObserver) { slideObserver.disconnect(); slideObserver = null; }
  const scrollEl = document.getElementById('stViewerScroll');
  if (!scrollEl) return;

  // The "active" slide is the one whose centre crosses the centre of the
  // scroll viewport. rootMargin of -40% top and -40% bottom carves out a
  // 20% band in the middle; when a card overlaps that band it activates.
  slideObserver = new IntersectionObserver(entries => {
    let best = null;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
    }
    if (!best) return;
    const idx = Number(best.target.dataset.slideIndex);
    if (!Number.isFinite(idx)) return;
    if (viewerState && idx !== viewerState.index) {
      viewerState.index = idx;
      applySlide(idx);
      updateActiveMarkers(idx);
    }
  }, {
    root: scrollEl,
    rootMargin: '-40% 0px -40% 0px',
    threshold: [0, 0.25, 0.5, 0.75, 1],
  });

  scrollEl.querySelectorAll('[data-slide-index]').forEach(el => slideObserver.observe(el));

  // Fade the keyboard hint once the user starts scrolling.
  scrollEl.addEventListener('scroll', () => {
    document.getElementById('stVnHint')?.classList.add('fade');
  }, { once: true, passive: true });
}

function updateActiveMarkers(idx) {
  document.querySelectorAll('.st-slide-card').forEach((el, i) => {
    el.classList.toggle('is-active', i === idx);
  });
  document.querySelectorAll('.st-rail-dot').forEach((dot, i) => {
    dot.classList.toggle('active', i === idx);
  });
}

function scheduleHintFade() {
  clearTimeout(hintFadeTimer);
  hintFadeTimer = setTimeout(() => {
    document.getElementById('stVnHint')?.classList.add('fade');
  }, 5000);
}

function viewerClickHandler(e) {
  if (e.target.closest('[data-action="closeViewer"]')) { closeViewer(); return; }
  const jump = e.target.closest('[data-jump-slide]');
  if (jump) { jumpToSlide(+jump.dataset.jumpSlide); return; }
}

function jumpToSlide(index) {
  if (!viewerState) return;
  if (index < 0 || index >= viewerState.story.slides.length) return;
  scrollToSlide(index);
}

function scrollToSlide(idx) {
  const target = document.querySelector(`[data-slide-index="${idx}"]`);
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function viewerKeyHandler(e) {
  if (!document.getElementById('storyViewer')) return;
  if (e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === ' ') {
    e.preventDefault(); advance(+1);
  } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
    e.preventDefault(); advance(-1);
  } else if (e.key === 'Escape') {
    closeViewer();
  }
}

function advance(dir) {
  if (!viewerState) return;
  const next = (viewerState.index ?? 0) + dir;
  const total = viewerState.story.slides.length;
  if (next < 0 || next >= total) return;
  scrollToSlide(next);
}

function closeViewer() {
  document.removeEventListener('keydown', viewerKeyHandler);
  if (slideObserver) { slideObserver.disconnect(); slideObserver = null; }
  clearTimeout(hintFadeTimer);
  document.getElementById('storyViewer')?.remove();
  viewerState = null;
  exitImmersive();
  // If we came here from the editor (Preview button), restore the dock
  // so authors can keep composing where they left off.
  if (editorState) showEditorDock();
}

async function applySlide(index) {
  const slide = viewerState?.story.slides[index];
  if (!slide?.map) return;
  await applyMapState(slide.map);
}

// =========================================================================
// Helpers
// =========================================================================

async function ensureLayerIndex() {
  if (allLayers.length === 0) {
    try { allLayers = await getAllLayersFn(); } catch { allLayers = []; }
  }
}

async function applyMapState(state) {
  if (!state) return;

  // 1. Switch basemap first if it changed. switchBasemap nudges zoom to force
  // a tile re-fetch (see map/basemap.js); doing this BEFORE flyTo avoids
  // the nudge interrupting the camera animation mid-flight.
  const curBasemap = getCurrentBasemapRef?.();
  if (state.basemap && switchBasemapRef && state.basemap !== curBasemap) {
    const btn = document.querySelector(`[data-basemap="${state.basemap}"]`);
    if (btn) {
      switchBasemapRef(mapRef, state.basemap, btn);
      // The nudge uses a 50ms setTimeout — wait a touch longer.
      await new Promise(r => setTimeout(r, 90));
    }
  }

  // 2. Camera move — duration scales with distance + zoom delta so close
  // hops feel snappy and continental jumps don't blur past.
  const cur = mapRef.getCenter();
  const dx = cur.lng - state.center[0];
  const dy = cur.lat - state.center[1];
  const distDeg = Math.sqrt(dx * dx + dy * dy);
  const zoomDelta = Math.abs(mapRef.getZoom() - state.zoom);
  const duration = Math.min(2400, Math.max(900, 700 + distDeg * 70 + zoomDelta * 240));

  mapRef.flyTo({
    center: state.center, zoom: state.zoom,
    pitch: state.pitch || 0, bearing: state.bearing || 0,
    duration, essential: true, curve: 1.42,
  });

  // 3. Layers — toggle in parallel with the fly so they're ready on landing.
  const want = new Set(state.visible_layers || []);
  const have = new Set(userLayersRef.loaded.keys());
  for (const id of have) if (!want.has(id)) userLayersRef.hide(id);
  for (const id of want) {
    if (!userLayersRef.isLoaded(id)) {
      try {
        const layer = await getLayer(id);
        await userLayersRef.show(layer);
      } catch (e) { /* layer may have been deleted; skip */ }
    } else {
      userLayersRef._setVisibility(id, true);
    }
  }
}

export function closeStories() {
  hide();
  closeEditorDock();
  closeViewer();      // closeViewer also exits immersive
  editorState = null;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
